import { Button, Form, FormInstance } from 'antd';
import CollapsibleCard from 'components/CollapsibleCard';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CategorieVehicule,
  CategorieVehiculeBus,
  CategorieVehiculeLourd,
  ClientType,
  NouveauDossierConcessionnaireData,
  RAI,
  UsageVehiculeParticulier,
  VehiculeType,
} from 'types/Concessionnaire';
import Step2CommandeSectionFields from 'views/dossiers/concessionnaire/steps/step2/Step2CommandeSectionFields';
import Step2QuestionsSectionFields from 'views/dossiers/concessionnaire/steps/step2/Step2QuestionsSectionFields';
import {
  getCommandeSectionFields,
  getQuestionsSectionFields,
  isBus,
  isPersonnePhysique,
  isVehiculeLourd,
  shouldAskPersonneMoraleFabricationEuropeQuestion,
} from 'views/dossiers/concessionnaire/steps/step2/step2.selectors';
import useStep2PersonnePhysiqueFlow from 'views/dossiers/concessionnaire/steps/step2/useStep2PersonnePhysiqueFlow';
import useStep2SectionSubmittable from 'views/dossiers/concessionnaire/steps/step2/useStep2SectionSubmittable';

interface Props {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  rai?: RAI;
  // Deprecated: navigation/actions are handled by MultiStepModal footer.
  goToPreviousStep?: () => void;
  isLoading?: boolean;
  error?: Error | null;
  showActions?: boolean;
  showError?: boolean;
  onSubmitAvailabilityChange?: (isSubmittable: boolean) => void;
  onDevisChange?: (files?: NouveauDossierConcessionnaireData['devis']) => void;
  onOtherFilesChange?: (
    files?: NouveauDossierConcessionnaireData['otherFiles'],
  ) => void;
}

const PERSONNE_MORALE_STEP2_FIELDS_COMMON: (keyof NouveauDossierConcessionnaireData)[] =
  [
    'nbDevis',
    'devis',
    'categorieVehicule',
    'nbVehicules',
    'usageVehicules',
    'dateLivraison',
  ];
const STEP2_ALWAYS_REQUIRED_FIELDS: (keyof NouveauDossierConcessionnaireData)[] =
  ['dateLivraison'];

function getPersonneMoraleStep2Fields(rai?: RAI, vehiculeType?: VehiculeType) {
  const dateField: keyof NouveauDossierConcessionnaireData =
    rai === '3.1' ? 'dateDevis' : 'dateEditionDevis';
  const fabricationEuropeFields =
    shouldAskPersonneMoraleFabricationEuropeQuestion(vehiculeType)
      ? (['produitEnEurope'] as (keyof NouveauDossierConcessionnaireData)[])
      : [];

  if (rai === '3.2') {
    return [
      ...PERSONNE_MORALE_STEP2_FIELDS_COMMON,
      ...fabricationEuropeFields,
      dateField,
      'datePrevisionnelleDevis',
    ] as (keyof NouveauDossierConcessionnaireData)[];
  }

  return [
    ...PERSONNE_MORALE_STEP2_FIELDS_COMMON,
    ...fabricationEuropeFields,
    dateField,
  ] as (keyof NouveauDossierConcessionnaireData)[];
}

function isFilledValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null && value !== '';
}

function areFieldsFilled(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  fields: (keyof NouveauDossierConcessionnaireData)[],
) {
  return fields.every((field) => isFilledValue(form.getFieldValue(field)));
}

function areFieldsValid(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  fields: (keyof NouveauDossierConcessionnaireData)[],
) {
  const hasErrors = form
    .getFieldsError(fields)
    .some((fieldState) => fieldState.errors.length > 0);
  const hasValidating = fields.some((field) => form.isFieldValidating(field));

  return !hasErrors && !hasValidating;
}

function isSectionSubmittable(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  fields: (keyof NouveauDossierConcessionnaireData)[],
) {
  return areFieldsFilled(form, fields) && areFieldsValid(form, fields);
}

function getFilledFieldNames(
  form: FormInstance<NouveauDossierConcessionnaireData>,
) {
  return Object.keys(form.getFieldsValue()).filter((key) => {
    const value = form.getFieldValue(
      key as keyof NouveauDossierConcessionnaireData,
    );

    return Array.isArray(value) ? value.length > 0 : !!value;
  });
}

export default function Step2({
  form,
  rai,
  onSubmitAvailabilityChange,
  onDevisChange,
  onOtherFilesChange,
}: Props) {
  const { t } = useTranslation();

  const typeClient = form.getFieldValue('typeClientFinal') as
    | ClientType
    | undefined;
  const vehiculeType = form.getFieldValue('vehiculeType') as
    | VehiculeType
    | undefined;

  const personnePhysique = isPersonnePhysique(typeClient);
  const isVehiculeLourdValue = isVehiculeLourd(vehiculeType);
  const isBusValue = isBus(vehiculeType);

  const categorieVehicule = Form.useWatch('categorieVehicule', form) as
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd
    | undefined;
  const usageVehicule = Form.useWatch('usageVehiculeParticulier', form) as
    | UsageVehiculeParticulier
    | undefined;

  const commandeSectionFields = useMemo(
    () => getCommandeSectionFields(personnePhysique),
    [personnePhysique],
  );
  const questionsSectionFields = useMemo(
    () =>
      getQuestionsSectionFields({
        personnePhysique,
        categorieVehicule,
        usageVehicule,
      }),
    [categorieVehicule, personnePhysique, usageVehicule],
  );
  const personneMoraleStep2Fields = useMemo(
    () => getPersonneMoraleStep2Fields(rai, vehiculeType),
    [rai, vehiculeType],
  );
  const fieldsToValidateForStep2 = useMemo(
    () =>
      personnePhysique
        ? [...commandeSectionFields, ...questionsSectionFields]
        : personneMoraleStep2Fields,
    [
      commandeSectionFields,
      personneMoraleStep2Fields,
      personnePhysique,
      questionsSectionFields,
    ],
  );
  const uniqueStep2Fields = useMemo(
    () =>
      Array.from(
        new Set([...fieldsToValidateForStep2, ...STEP2_ALWAYS_REQUIRED_FIELDS]),
      ),
    [fieldsToValidateForStep2],
  );
  const isFormValid = useStep2SectionSubmittable({
    form,
    fields: uniqueStep2Fields,
  });

  const flow = useStep2PersonnePhysiqueFlow({
    form,
    personnePhysique,
    commandeSectionFields,
    questionsSectionFields,
  });

  const isStepSubmittable = personnePhysique
    ? flow.isCommandeSectionSaved && flow.isQuestionsSectionSaved
    : isFormValid;
  const hasValidatedFilledFieldsRef = useRef(false);

  // (Re)Validation des champs remplis.
  // Dans le cas ou l'utilisateur clique sur "Precedent" puis "Suivant".
  // Et synchronisation de l'état "submittable" vers le parent.
  useEffect(() => {
    if (!hasValidatedFilledFieldsRef.current) {
      hasValidatedFilledFieldsRef.current = true;
      form.validateFields(getFilledFieldNames(form)).catch(() => undefined);
    }

    onSubmitAvailabilityChange?.(isStepSubmittable);
  }, [form, isStepSubmittable, onSubmitAvailabilityChange]);

  return (
    <Form
      form={form}
      name="step2"
      layout="vertical"
      className="[&_label]:font-bold [&_p]:font-bold"
      onValuesChange={flow.handleValuesChange}
    >
      {personnePhysique ? (
        <div className="space-y-4">
          <CollapsibleCard
            title={t('nouveau_formulaire:bon_commande_section_title')}
            isOpen={flow.isCommandeSectionOpen}
            isComplete={flow.isCommandeSectionSaved}
            onToggle={
              flow.isCommandeSectionSaved
                ? flow.toggleCommandeSection
                : undefined
            }
          >
            <div className="space-y-4 mt-4">
              <Step2CommandeSectionFields
                form={form}
                rai={rai}
                personnePhysique={personnePhysique}
                onDevisChange={onDevisChange}
                onOtherFilesChange={onOtherFilesChange}
              />
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Button
                    type="primary"
                    htmlType="button"
                    onClick={flow.handleSaveCommandeSection}
                    shape="round"
                    disabled={
                      !isSectionSubmittable(form, commandeSectionFields)
                    }
                  >
                    {t('global:enregistrer_et_continuer')}
                  </Button>
                )}
              </Form.Item>
            </div>
          </CollapsibleCard>

          <CollapsibleCard
            title={t(
              'nouveau_formulaire:questions_achat_vehicules_neufs_section_title',
            )}
            isOpen={flow.isQuestionsSectionOpen}
            showContent={flow.isCommandeSectionSaved}
            isComplete={flow.isQuestionsSectionSaved}
            onToggle={
              flow.isQuestionsSectionSaved
                ? flow.toggleQuestionsSection
                : undefined
            }
          >
            <div className="space-y-4 mt-4">
              <Step2QuestionsSectionFields
                form={form}
                personnePhysique={personnePhysique}
                vehiculeType={vehiculeType}
                isVehiculeLourd={isVehiculeLourdValue}
                isBus={isBusValue}
                categorieVehicule={categorieVehicule}
                usageVehicule={usageVehicule}
              />
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Button
                    type="primary"
                    htmlType="button"
                    onClick={flow.handleSaveQuestionsSection}
                    shape="round"
                    disabled={
                      !isSectionSubmittable(form, questionsSectionFields)
                    }
                  >
                    {t('global:enregistrer_et_continuer')}
                  </Button>
                )}
              </Form.Item>
            </div>
          </CollapsibleCard>
        </div>
      ) : (
        <>
          <Step2CommandeSectionFields
            form={form}
            rai={rai}
            personnePhysique={personnePhysique}
            onDevisChange={onDevisChange}
            onOtherFilesChange={onOtherFilesChange}
          />
          <Step2QuestionsSectionFields
            form={form}
            personnePhysique={personnePhysique}
            vehiculeType={vehiculeType}
            isVehiculeLourd={isVehiculeLourdValue}
            isBus={isBusValue}
            categorieVehicule={categorieVehicule}
            usageVehicule={usageVehicule}
          />
        </>
      )}
    </Form>
  );
}
